import { Keyboard } from "@eventr/capacitor-keyboard";
import { NativeNavigation } from "@eventr/native/navigation";
import {
  AUTH_ROOT_PATH,
  AUTH_STACK_ID,
  desiredNativeRoot,
  isRootSwapInFlight,
  POP_TO_ROOT_COUNT,
  presentedNativeRoot,
} from "./NativeRootController";
import { viewOptionsForPath } from "./init";
import { isIosNativeNavigation } from "./platform";

/**
 * The native half of `replacePath`. The plugin swaps the top view and leaves the stack
 * beneath alive, so on the pre-auth stack a replace has to be a reset.
 *
 * Returns true when the native layer has handled the navigation, including when it has
 * declined to navigate, so the caller must not also drive the router.
 */
export async function replaceOnNativeRoot(path: string): Promise<boolean> {
  if (!isIosNativeNavigation()) return false;

  // `presentedNativeRoot` still names the outgoing root across `reset()`, so acting on it
  // mid-swap would pop a stack that is already gone.
  if (isRootSwapInFlight()) return true;

  if (presentedNativeRoot() !== "auth") return false;

  // Session in hand but the pre-auth root still up: NativeRootController owns the transition,
  // and the destination sits on the root being torn down.
  if (desiredNativeRoot() === "tabs") return true;

  return resetAuthStack(path);
}

/**
 * Pop the pre-auth stack back to the welcome view, then land on `path` unless that is the
 * root itself. Returns false if the plugin refused, so the caller can fall back to a web
 * replace rather than leave the user parked on the screen that just failed.
 */
async function resetAuthStack(path: string): Promise<boolean> {
  try {
    // A stack reset does not resign first responder on its own.
    await Keyboard.hide().catch(() => {});

    // Pop rather than re-root with `push({ mode: "root" })`: AUTH_ROOT_PATH is not in the
    // registry, so a re-pushed "/" arrives wearing the nav bar that welcome's launch-time
    // `stackItem` hides. Unanimated so welcome never paints.
    await NativeNavigation.pop({ stack: AUTH_STACK_ID, count: POP_TO_ROOT_COUNT, animated: false });

    if (path !== AUTH_ROOT_PATH) {
      await NativeNavigation.push({
        target: AUTH_STACK_ID,
        component: { type: "view", path, ...viewOptionsForPath(path) },
        animated: false,
      });
    }

    syncBrowserLocation(path);
    return true;
  } catch (error) {
    console.error("Auth stack reset failed", error);
    return false;
  }
}

/**
 * The plugin moves the native stack without touching `window.location`, which both the push
 * and replace paths read to pick a slide direction. Each native view has its own controlled
 * `<Router>`, so correcting the global provokes no navigation.
 */
function syncBrowserLocation(path: string): void {
  try {
    window.history.replaceState(window.history.state, "", path);
  } catch (error) {
    // The stack has already moved; a rejected path is not worth losing that.
    console.error("Could not sync location after auth stack reset", error);
  }
}
